import React, { useCallback, useEffect, useMemo, useState } from "react";
import SelectGroup from "../../witadmin/CRUD/Common/SelectGroup";
import styled from "styled-components";
import { usePropertyInventoryData } from "../../../components/inventory-provider";
import InputGroup from "../../witadmin/CRUD/Common/InputGroup";
import { FieldProps } from "react-jsonschema-form";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import MultiCheckBoxGroup from "../../witadmin/CRUD/Common/MultiCheckBoxGroup";

const messages = defineMessages({
  parentTicker: {
    id: "inventoryTable.parentTicker",
    description: "parentTicker",
    defaultMessage: "parentTicker",
  },
  constant: {
    id: "inventoryTable.constant",
    description: "constant",
    defaultMessage: "constant",
  },
  own: {
    id: "inventoryTable.own",
    description: "own",
    defaultMessage: "own",
  },
  formula: {
    id: "inventoryTable.formula",
    description: "formula",
    defaultMessage: "formula",
  },
  days: {
    id: "inventoryTable.days",
    description: "days",
    defaultMessage: "days",
  },
  unlimited: {
    id: "inventoryTable.unlimited",
    description: "unlimited",
    defaultMessage: "unlimited",
  },
  monday: {
    id: "inventoryTable.monday",
    description: "Monday",
    defaultMessage: "Monday",
  },
  tuesday: {
    id: "inventoryTable.tuesday",
    description: "Tuesday",
    defaultMessage: "Tuesday",
  },
  wednesday: {
    id: "inventoryTable.wednesday",
    description: "Wednesday",
    defaultMessage: "Wednesday",
  },
  thursday: {
    id: "inventoryTable.thursday",
    description: "Thursday",
    defaultMessage: "Thursday",
  },
  friday: {
    id: "inventoryTable.friday",
    description: "Friday",
    defaultMessage: "Friday",
  },
  saturday: {
    id: "inventoryTable.saturday",
    description: "Saturday",
    defaultMessage: "Saturday",
  },
  sunday: {
    id: "inventoryTable.sunday",
    description: "Sunday",
    defaultMessage: "Sunday",
  },
});

const SelectGroupWrapper = styled.div`
  display: flex;
  margin-bottom: -20px;
  & > * {
    max-width: 200px;
    min-width: 200px;
  }
  & > *:first-child {
    margin-right: 20px;
  }
`;

const fieldsWithUnlimitedOption = [
  "minStay",
  "maxStay",
  "minNotice",
  "maxNotice",
  "closeToArrival",
  "closeToDeparture",
  "rackRate",
];

const TYPE_VALUES = {
  OWN: "own",
  UNLIMITED: "unlimited",
  PARENT_TICKER: "parentTicker",
  CONSTANT: "constant",
  FORMULA: "formula",
  DAYS: "days",
};

const OWN_DATA = "Propia";

export function InventoryStateSelector({
  formData,
  onChange,
  id,
  schema,
  name,
  ...rest
}: FieldProps) {
  const inventories = usePropertyInventoryData();
  const intl = useIntl();

  const [inventoryOptions, setInventoryOptions] = useState<
    undefined | { [key: string]: { label: string } }
  >();
  const [typeValue, setTypeValue] = useState<string | undefined>(() => {
    let type = Object.keys(formData).find(key => {
      if (formData[key] !== "" && formData[key] !== undefined && formData[key] !== null) {
        return key;
      }
      return undefined;
    });

    if ((!type && !formData.constant) || formData.constant === 0) {
      type = TYPE_VALUES.UNLIMITED;
    }

    return type;
  });

  useEffect(() => {
    if (!inventoryOptions && inventories?.length) {
      const inv = inventories[0].inventoryTickers?.reduce(
        (accum, curr) => ({ ...accum, [curr.ticker]: { label: curr.ticker } }),
        {}
      );
      setInventoryOptions(inv);
    }
  }, [inventories, inventoryOptions]);

  const handleTypeChange = useCallback(
    event => {
      setTypeValue(event.target.value);
      const data = { ...formData };
      Object.keys(data).forEach(key => (data[key] = key === TYPE_VALUES.DAYS ? [] : ""));

      if (event.target.value === TYPE_VALUES.UNLIMITED) {
        onChange(data);
      }
      if (event.target.value === TYPE_VALUES.OWN) {
        data.own = OWN_DATA;
        onChange(data);
      }
      if (event.target.value === TYPE_VALUES.PARENT_TICKER) {
        if (inventoryOptions) {
          data.parentTicker = Object.values(inventoryOptions)[0].label;
        }
        onChange(data);
      }
    },
    [setTypeValue, inventoryOptions, formData, onChange]
  );

  const handleChange = useCallback(
    (event, type) => {
      const data = { ...formData };
      Object.keys(data).forEach(key => (data[key] = key === TYPE_VALUES.DAYS ? [] : ""));

      if (type === TYPE_VALUES.PARENT_TICKER) {
        data.parentTicker = event.target.value;
      }
      if (type === TYPE_VALUES.CONSTANT) {
        data.constant = Number(event.target.value);
      }
      if (type === TYPE_VALUES.FORMULA) {
        data.formula = event.target.value;
      }
      onChange(data);
    },
    [formData, onChange]
  );

  const handleDaysChange = useCallback(
    ({ value }) => {
      const data = { ...formData };
      data.days = value;
      onChange(data);
    },
    [formData, onChange]
  );

  const typeOptions = useMemo(() => {
    const allOptionsObject: { [key: string]: {} } = {
      parentTicker: {
        label: intl.formatMessage(messages.parentTicker),
      },
      constant: {
        label: intl.formatMessage(messages.constant),
      },
      own: {
        label: intl.formatMessage(messages.own),
      },

      formula: {
        label: intl.formatMessage(messages.formula),
      },
      days: {
        label: intl.formatMessage(messages.days),
      },
      unlimited: {
        label: intl.formatMessage(messages.unlimited),
      },
    };

    const optionsObject: { [key: string]: {} } = {};
    Object.keys(formData).forEach(key => (optionsObject[key] = allOptionsObject[key]));

    if (fieldsWithUnlimitedOption.includes(name)) {
      optionsObject.unlimited = allOptionsObject.unlimited;
    }
    return optionsObject;
  }, [formData, name, intl]);

  return (
    <>
      <label>{schema?.title}</label>
      <SelectGroupWrapper>
        <SelectGroup
          value={typeValue}
          field={{
            fieldName: "type",
            widget: "selector",
            optionDisplay: typeOptions,
          }}
          onChange={({ event }) => handleTypeChange(event)}
          errors={undefined}
          additionalFields={undefined}
          className={undefined}
        />
        {typeValue === TYPE_VALUES.PARENT_TICKER && inventoryOptions && (
          <SelectGroup
            value={formData?.parentTicker}
            field={{
              fieldName: "type",
              widget: "selector",
              optionDisplay: inventoryOptions,
            }}
            onChange={({ event }) => handleChange(event, "parentTicker")}
            errors={undefined}
            additionalFields={undefined}
            className={undefined}
          />
        )}
        {typeValue === TYPE_VALUES.CONSTANT && (
          <InputGroup
            type={"number"}
            value={formData.constant}
            field={{ fieldName: `inventory-state-selector-input` }}
            onChange={({ event }) => handleChange(event, "constant")}
            disabled={false}
            readOnly={false}
          />
        )}
        {typeValue === TYPE_VALUES.FORMULA && (
          <InputGroup
            type={"string"}
            value={formData.formula}
            field={{ fieldName: `inventory-state-selector-input` }}
            onChange={({ event }) => handleChange(event, "formula")}
            disabled={false}
            readOnly={false}
          />
        )}
        {typeValue === TYPE_VALUES.DAYS && (
          <MultiCheckBoxGroup
            horizontal={true}
            className="witadmin-bs3"
            value={formData?.days}
            onChange={handleDaysChange}
            field={{
              disabled: false,
              fieldName: `inventory-state-selector-input-days`,
              options: [
                {
                  ticker: 0,
                  name: <FormattedMessage {...messages.monday} />,
                },
                {
                  ticker: 1,
                  name: <FormattedMessage {...messages.tuesday} />,
                },
                {
                  ticker: 2,
                  name: <FormattedMessage {...messages.wednesday} />,
                },
                {
                  ticker: 3,
                  name: <FormattedMessage {...messages.thursday} />,
                },
                {
                  ticker: 4,
                  name: <FormattedMessage {...messages.friday} />,
                },
                {
                  ticker: 5,
                  name: <FormattedMessage {...messages.saturday} />,
                },
                {
                  ticker: 6,
                  name: <FormattedMessage {...messages.sunday} />,
                },
              ],
            }}
          />
        )}
      </SelectGroupWrapper>
    </>
  );
}
